import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Button } from "@visioni/a3s-ui/components";
import { CompilationFilterRow, type CompilationFilters } from "../CompilationFilterRow";
import { CompilationList, type CompilationListItem } from "../CompilationList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClipCompilationPageProps<TSite extends object, TCamera extends object> {
  readonly items: readonly CompilationListItem[];
  readonly isLoading?: boolean | undefined;
  readonly error?: boolean | undefined;
  readonly totalCount?: number | undefined;
  readonly hasNextPage?: boolean | undefined;
  readonly isFetchingNextPage?: boolean | undefined;
  readonly onLoadMore?: (() => void) | undefined;
  readonly onWatch?: ((item: CompilationListItem) => void) | undefined;
  readonly onDownload?: ((item: CompilationListItem) => void) | undefined;
  /** Omit to hide the "Request Compilation" button entirely (e.g. non-support users). */
  readonly onRequestCompilation?: (() => void) | undefined;
  readonly filters: CompilationFilters;
  readonly onFiltersChange: (filters: CompilationFilters) => void;
  readonly sites: readonly TSite[];
  readonly cameras: readonly TCamera[];
  readonly getSiteLabel: (site: TSite) => string;
  readonly getSiteId: (site: TSite) => string;
  readonly getCameraLabel: (camera: TCamera) => string;
  readonly getCameraId: (camera: TCamera) => string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ClipCompilationPageInner<TSite extends object, TCamera extends object>({
  items,
  isLoading,
  error,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onWatch,
  onDownload,
  onRequestCompilation,
  filters,
  onFiltersChange,
  sites,
  cameras,
  getSiteLabel,
  getSiteId,
  getCameraLabel,
  getCameraId,
}: ClipCompilationPageProps<TSite, TCamera>) {
  return (
    <View style={styles.page}>
      {onRequestCompilation && (
        <View style={styles.header}>
          <Button label="Request Compilation" variant="primary" size="sm" onPress={onRequestCompilation} />
        </View>
      )}

      <View style={styles.filterRow}>
        <CompilationFilterRow
          filters={filters}
          onFiltersChange={onFiltersChange}
          sites={sites}
          cameras={cameras}
          getSiteLabel={getSiteLabel}
          getSiteId={getSiteId}
          getCameraLabel={getCameraLabel}
          getCameraId={getCameraId}
        />
      </View>

      <View style={styles.list}>
        <CompilationList
          items={items}
          isLoading={isLoading}
          error={error}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={onLoadMore}
          onWatch={onWatch}
          onDownload={onDownload}
        />
      </View>
    </View>
  );
}

ClipCompilationPageInner.displayName = "ClipCompilationPage";

const ClipCompilationPage = ClipCompilationPageInner as <TSite extends object, TCamera extends object>(
  props: ClipCompilationPageProps<TSite, TCamera>,
) => ReturnType<typeof ClipCompilationPageInner>;

export { ClipCompilationPage };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  page: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    zIndex: 2,
  },
  filterRow: {
    paddingHorizontal: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    zIndex: 1,
  },
  list: {
    flex: 1,
  },
}));
